/* ========================================================================== *\
    IMPORTS
\* ========================================================================== */
import { isNilOrEmpty } from "./isNilOrEmpty/isNilOrEmpty";
import { Action } from './Action/Action';
/* ========================================================================== *\
    PRIVATE VARIABLES
\* ========================================================================== */
const defaultSubscribeOptions = {
    callCount: 0,
    maxCallCount: Infinity
}, subscribers = new Map();
/* ========================================================================== *\
    PRIVATE METHODS
\* ========================================================================== */
/**
 * Takes the name of an event and applies the scope to it when a scope
 * is provided.
 *
 * @param event The base name of the event.
 * @param scope An optional scope to add to the base event name.
 *
 * @returns Returns the name of the event.
 */
function getEventName(event, scope) {
    return (isNilOrEmpty(scope))
        ? event
        : `${event}${scope}`;
}
/**
 * Returns the subscribers for an event.
 *
 * @param event The name of the event (scoped) whose subscriptions should
 *        be returned.
 * @param registerNewEvent Be default an unknown event will not be added to the
 *        internal administration of events. When set to true an unknown event
 *        will be added to the administration.
 */
function getEventSubscriptions(event, registerNewEvent = false) {
    var _a;
    const isNewEvent = !subscribers.has(event), subscriptions = (_a = subscribers.get(event)) !== null && _a !== void 0 ? _a : new Map();
    if (isNewEvent && registerNewEvent) {
        subscribers.set(event, subscriptions);
    }
    return subscriptions;
}
function once(event, callback, options) {
    const subscribeOptions = { ...options, maxCallCount: 1 };
    return subscribe(event, callback, subscribeOptions);
}
function publish(event, detail, options) {
    const eventName = getEventName(event, options === null || options === void 0 ? void 0 : options.scope), subscriptions = getEventSubscriptions(eventName), action = new Action(detail, options === null || options === void 0 ? void 0 : options.cancellable);
    subscriptions.forEach((options, subscriber) => {
        // First adjust the call count so if the subscriber throws an exception
        // the internal administration has already been taken care of.
        options.callCount++;
        if (options.callCount >= options.maxCallCount) {
            unsubscribe(eventName, subscriber);
        }
        subscriber(action);
    });
    return action;
}
function subscribe(event, callback, options) {
    const subscribeOptions = { ...defaultSubscribeOptions, ...options };
    if (typeof callback !== 'function' ||
        subscribeOptions.maxCallCount <= 0) {
        return false;
    }
    const eventName = getEventName(event, subscribeOptions.scope), subscriptions = getEventSubscriptions(eventName, true);
    if (subscriptions.has(callback)) {
        return false;
    }
    subscriptions.set(callback, subscribeOptions);
    return true;
}
function unsubscribe(event, callback, scope) {
    if (typeof callback !== 'function') {
        return false;
    }
    const eventName = getEventName(event, scope), subscriptions = getEventSubscriptions(eventName);
    return subscriptions.delete(callback);
}
/* ========================================================================== *\
    EXPORTS
\* ========================================================================== */
export * from './Action/Action';
export { once, publish, subscribe, unsubscribe };
